import { removeNonCaleoStorage } from 'utils/utils'
import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import {
  beginAzureBootstrap,
  acquireSilentAzureAccessToken,
  clearAuthSession,
  failAzureBootstrap,
  getAuthSessionMode,
} from '../hooks/authSession'
import { getCsrfCookieToken } from './csrf'

let refreshInFlight: Promise<void> | null = null

type RetryableAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean
}

const isLoginPage = () => window.location.pathname.includes('login')

/**
 * A refresh (and /azure/id) sets a new CSRF cookie, but the request being retried still carries
 * the token from when it was built; caleo-back would reject it with 403 "Invalid CSRF token".
 *
 * @param request - The request about to be retried.
 * @notExported
 */
const setFreshCsrfToken = (request: RetryableAxiosRequestConfig) => {
  const csrfToken = getCsrfCookieToken()
  if (csrfToken && request.headers) {
    request.headers['x-csrf-token'] = csrfToken
  }
}

const redirectToLogin = () => {
  if (!isLoginPage()) {
    window.location.replace('/login')
  }
}

async function refreshSession(apiProvider: AxiosInstance): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = apiProvider
      .post('/login/refresh', {}, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null
      })
  }

  return refreshInFlight
}

function redirectWithCleanup(error: unknown) {
  clearAuthSession()
  removeNonCaleoStorage()
  redirectToLogin()
  return Promise.reject(error)
}

export const interceptorError = async (error: AxiosError<{ key?: string }>, apiProvider: AxiosInstance) => {
  const originalRequest = error.config as RetryableAxiosRequestConfig

  const rememberRedirect = () => {
    const path = window.location.pathname
    const isErrorPage = path === '/404' || path === '/403'
    if (path && !path.includes('login') && !path.includes('logout') && !isErrorPage) {
      const target = path + window.location.search
      window.sessionStorage.setItem('lastNonLoginPath', target)
    }
  }

  if (error.name === 'AbortError' || error.name === 'CanceledError') {
    return Promise.reject(error)
  }

  if (error?.response?.status === 403 && originalRequest.url === `/login/refresh` && getAuthSessionMode() !== 'azure') {
    removeNonCaleoStorage()
    rememberRedirect()
    redirectToLogin()
    return Promise.reject(error)
  }

  if (error?.response?.status === 403 && originalRequest.url !== `/login/refresh`) {
    const { router } = await import('../router')
    router.navigate('/403')
    return Promise.reject(error)
  }

  if (error?.response?.status === 404) {
    const hasAuth = document.cookie.includes('caleoAccessToken=')
    // Only redirect to /404 when we appear authenticated; otherwise let normal auth flow handle login
    if (hasAuth) {
      const { router } = await import('../router')
      router.navigate('/404', { state: { url: '/404' } })
    }
    return Promise.reject(error)
  }

  // if 401 refresh token and retry
  if (error?.response?.status === 401) {
    const sessionMode = getAuthSessionMode()

    // if logout unsuccessful reject
    if (originalRequest.url === `/logout`) {
      return redirectWithCleanup(error)
    }

    if (originalRequest.url === `/login/refresh`) {
      return Promise.reject(error)
    }

    // While on the login page, avoid refresh attempts for unauthenticated requests.
    if (isLoginPage()) {
      return redirectWithCleanup(error)
    }

    if (originalRequest._retry) {
      rememberRedirect()
      return redirectWithCleanup(error)
    }

    originalRequest._retry = true

    try {
      await refreshSession(apiProvider)
      setFreshCsrfToken(originalRequest)
      return await apiProvider.request(originalRequest)
    } catch (refreshError) {
      if (sessionMode !== 'azure') {
        rememberRedirect()
        return redirectWithCleanup(refreshError)
      }

      try {
        const azureBootstrap = beginAzureBootstrap()
        const azureAccessToken = await acquireSilentAzureAccessToken()
        await azureBootstrap

        // Most requests are cookie-authenticated and carry no Authorization header,
        // so the fresh Azure token only helps once it re-mints the backend's own
        // session cookies via /azure/id.
        await apiProvider.get('/azure/id', { headers: { Authorization: `Bearer ${azureAccessToken}` } })

        if (originalRequest.headers?.Authorization) {
          originalRequest.headers.Authorization = `Bearer ${azureAccessToken}`
        }
        setFreshCsrfToken(originalRequest)

        return await apiProvider.request(originalRequest)
      } catch (azureRefreshError) {
        // A cancelled retry (e.g. the page navigating away) tells us nothing about
        // whether the Azure session/cookie bootstrap actually succeeded, so reject
        // with the original error instead of reporting a false bootstrap outcome.
        if (
          azureRefreshError instanceof Error &&
          (azureRefreshError.name === 'AbortError' || azureRefreshError.name === 'CanceledError')
        ) {
          return Promise.reject(azureRefreshError)
        }
        failAzureBootstrap(azureRefreshError)
        rememberRedirect()
        return redirectWithCleanup(azureRefreshError)
      }
    }
  }

  if (error?.response?.status === 400 && error.response.data.key && error.response.data.key.includes('azureLogin')) {
    console.error('Azure login error!')
    return Promise.reject(error.response.data)
  }

  // if error is not yet handled, reject promise with error
  return Promise.reject(error)
}
